import { ApolloLink, Observable } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';

/**
 * ApolloLink que instrumenta operaciones GraphQL para Sentry.
 *
 * Responsabilidades:
 *  1. Crea un span inactivo (op: 'graphql.client') por cada operación
 *     (query | mutation | subscription) con name `${kind}.${operationName}`.
 *  2. Inyecta los headers `sentry-trace` y `baggage` en el contexto del
 *     request HTTP, derivados del span recién creado. `apollo-link-http`
 *     los propaga a `fetch`, lo que permite al backend continuar la traza
 *     distribuida con el span GraphQL como parent.
 *  3. Reporta los errores GraphQL (response.errors[]) con tags
 *     `graphql_op` y `graphql_kind` y extra `path` / `locations` /
 *     `extensions`.
 *
 * Decisiones de diseño:
 *  - NO captura network errors: el dashboard ya maneja `INVALID_TOKEN` en
 *    `apolloQuery` / `apolloMutation` (ver `src/apollo/apolloClient.js`),
 *    y duplicar la captura aquí generaría issues redundantes en Sentry.
 *    Si se requiere instrumentar network errors en el futuro, hacerlo en
 *    un link separado (`apollo-link-error`) — out of scope.
 *  - Usa `Sentry.startInactiveSpan` para no contaminar el active scope:
 *    Apollo dispara múltiples operaciones en paralelo y un span activo
 *    confundiría el contexto.
 *  - `spanToTraceHeader` / `spanToBaggageHeader` y `startInactiveSpan` se
 *    acceden defensivamente (typeof check): si Sentry no está inicializado
 *    todavía o cambia la API en una versión futura, el link sigue funcionando
 *    como passthrough (no rompe el request). Ver nota en `buildTraceHeaders`
 *    sobre por qué NO usamos `getTraceData` aquí.
 *
 * Subscriptions:
 *  - Para subscriptions (kind === 'subscription') NO inyectamos headers
 *    porque la operación viaja por `WebSocketLink` (no por HTTP), y la
 *    auth/headers se envían en `connectionParams` del WS, no por
 *    `operation.setContext`. La propagación de traza para subs queda
 *    como follow-up (requiere modificar `connectionParams` en apolloClient).
 *  - Queries / mutations: el span se cierra en `complete` / `error` /
 *    `unsubscribe`. En la práctica `complete` llega justo después del
 *    único `next`, así que el efecto neto es equivalente a "cerrar en
 *    la response".
 *  - Subscriptions: el span se cierra en el PRIMER `next` (establishment
 *    time: "subscribe → primer payload"). Mantenerlo abierto durante
 *    minutos/horas no es semántico en Sentry. Payloads posteriores siguen
 *    pasando por `reportGraphqlErrors` (con dedup) pero ya no tocan el
 *    span. El cierre en `unsubscribe`/`error` sigue siendo no-op
 *    (idempotente vía `state.closed`).
 *  - Para evitar inflar Sentry, los GraphQL errors en una sub solo se
 *    capturan UNA VEZ por par (operationName, message, path). Un servidor
 *    que devuelve el mismo error por cada payload no genera N issues.
 *
 * Wire-up esperado en `src/apollo/apolloClient.js` (Agente 5):
 *   import { from } from 'apollo-link';
 *   import { sentryApolloLink } from '../observability/apolloLink';
 *   const link = from([sentryApolloLink, authLink, splitLink]);
 */

// Sentry v8 SpanStatus codes. Ver @sentry/core/tracing/spanstatus.
// No se re-exporta desde @sentry/react ni @sentry/browser, así que lo
// declaramos local con la fuente documentada. Si Sentry cambia la API,
// `setStatus` se llama dentro de try/catch (best-effort) y un valor
// stale solo significa que el status no se reflejará — no rompe nada.
const SPAN_STATUS_ERROR = 2;

/**
 * Extrae kind ('query'|'mutation'|'subscription') de la query usando
 * `getMainDefinition` — el mismo helper que `apolloClient.js` ya usa
 * para hacer el `split` por subscriptions. Garantiza que ignoramos
 * `FragmentDefinition` y tomamos la `OperationDefinition` correcta.
 *
 * @param {Object} query - DocumentNode de la operación
 * @returns {'query'|'mutation'|'subscription'|'unknown'}
 */
function extractKind(query) {
  if (!query) return 'unknown';
  try {
    const def = getMainDefinition(query);
    if (def && def.kind === 'OperationDefinition' && def.operation) {
      return def.operation;
    }
  } catch (_e) {
    // best-effort: si el doc no tiene la forma esperada, fallback a unknown
  }
  return 'unknown';
}

/**
 * Construye los headers de propagación de traza desde Sentry, derivados
 * del span pasado. Si Sentry no está listo o el span es null, devuelve `{}`.
 *
 * Importante: pasamos `span` explícitamente a `spanToTraceHeader` y
 * `spanToBaggageHeader` para que el backend continúe la traza usando este
 * span como parent (no el transaction activo, que podría ser sibling de
 * varias operaciones GraphQL concurrentes).
 *
 * Nota sobre la API: en @sentry/browser v8.55.x los helpers exportados son
 * `spanToTraceHeader(span)` y `spanToBaggageHeader(span)` — NO existe un
 * `getTraceData` top-level (sí en @sentry/core, pero no re-exportado por
 * browser/react). Si una versión futura de Sentry expone `getTraceData`
 * en el bundle del cliente, esa ruta queda como follow-up para tener un
 * solo round-trip al SDK.
 *
 * @param {Object|null} span - Sentry span asociado a la operación
 * @returns {Object<string, string>}
 */
function buildTraceHeaders(span) {
  const traceHeaders = {};
  if (!span) return traceHeaders;
  try {
    if (typeof Sentry.spanToTraceHeader === 'function') {
      const sentryTrace = Sentry.spanToTraceHeader(span);
      if (sentryTrace) {
        traceHeaders['sentry-trace'] = sentryTrace;
      }
    }
    if (typeof Sentry.spanToBaggageHeader === 'function') {
      const baggage = Sentry.spanToBaggageHeader(span);
      if (baggage) {
        traceHeaders.baggage = baggage;
      }
    }
  } catch (_e) {
    // best-effort: si Sentry no está listo, no rompemos el request
  }
  return traceHeaders;
}

/**
 * Crea el span Sentry para una operación GraphQL. Devuelve `null` si la
 * API de spans no está disponible.
 */
function startGraphqlSpan(kind, name) {
  try {
    if (typeof Sentry.startInactiveSpan === 'function') {
      return Sentry.startInactiveSpan({
        name: `${kind}.${name}`,
        op: 'graphql.client',
        attributes: {
          'graphql.operation.kind': String(kind),
          'graphql.operation.name': String(name),
        },
      });
    }
  } catch (_e) {
    // best-effort: si la API de spans cambia, seguimos sin span
  }
  return null;
}

/**
 * Cierra un span de forma idempotente — `closed` flag evita doble-end
 * cuando concurren `complete` y `unsubscribe`.
 */
function finalizeSpan(state) {
  if (!state.span || state.closed) return;
  state.closed = true;
  try {
    if (typeof state.span.setAttribute === 'function') {
      state.span.setAttribute('duration_ms', Date.now() - state.startedAt);
    }
    if (typeof state.span.end === 'function') {
      state.span.end();
    }
  } catch (_e) {
    // best-effort cleanup
  }
}

/**
 * Errores GraphQL "esperados" del propio dashboard que NO deben generar
 * issues en Sentry. `INVALID_TOKEN` es la expiración natural de sesión y
 * ya tiene su propio handler (logout + redirect) en `apolloQuery` /
 * `apolloMutation` — capturarlo aquí solo genera ruido y quema cuota.
 *
 * Se hace match por `code` / `errorCode` en `err.extensions`. Fallback
 * por `message` con match EXACTO (trimmed eq — NO substring) para evitar
 * falso positivo en mensajes que mencionen el código como texto libre.
 * (algunos resolvers tiran el string del enum como message en vez de
 * poblar `extensions.code`)
 */
const EXPECTED_ERROR_CODES = new Set(['INVALID_TOKEN', 'UNAUTHENTICATED']);

function isExpectedGraphqlError(err) {
  if (!err) return false;
  const code =
    err.extensions && (err.extensions.code || err.extensions.errorCode);
  if (code && EXPECTED_ERROR_CODES.has(String(code))) return true;
  // Fallback por `message` cuando el resolver tira el enum como string en
  // vez de poblar `extensions.code`. Match exacto (eq o trimmed eq) — NO
  // substring para evitar falso positivo con mensajes que mencionen el
  // token literal (e.g. "audit logs contain INVALID_TOKEN events").
  const rawMsg = (err && err.message) || '';
  const msg = String(rawMsg).trim();
  if (EXPECTED_ERROR_CODES.has(msg)) return true;
  return false;
}

/**
 * El `message` de un error de GraphQL lo redacta el BACKEND, y suele incluir
 * el dato que provocó el fallo. Como ese message termina siendo el TÍTULO del
 * grupo de error en Sentry, cualquier dato de cliente que venga ahí queda
 * visible e indexado para todo el que tenga acceso al proyecto.
 *
 * Caso que originó esto (DASHBOARD-ZC-KQ): el backend responde
 * `You provided an ID that was not a valid MongoObjectId: <teléfono>` y el
 * teléfono del cliente acababa en el título del issue.
 *
 * Regla: el error debe decir QUÉ pasó, no CON QUÉ dato de cliente pasó.
 *
 * Se redactan tres formas, en este orden (importa: el correo puede contener
 * dígitos, y un ObjectId puede contener una corrida larga de dígitos que la
 * última regla partiría a la mitad):
 *
 *  1. Correos — salen en errores de unicidad ("email already exists").
 *  2. ObjectId de Mongo — identifica a una persona igual que un teléfono.
 *  3. Corridas de 7+ dígitos — teléfonos E.164 (10-15) y cualquier otro
 *     identificador numérico. El umbral deja pasar años, códigos HTTP,
 *     contadores y fechas, que sí son útiles para triage.
 *
 * Efecto secundario deseado: como el dato variable desaparece, N teléfonos
 * distintos dejan de ser N títulos distintos y colapsan en UN issue.
 *
 * Lo que se pierde en diagnóstico se compensa con `extra.path` y con el
 * `code` del backend, que `pickSafeExtensions` sí conserva.
 */
const PII_PATTERNS = [
  [/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[correo]'],
  [/\b[0-9a-fA-F]{24}\b/g, '[id]'],
  [/\d{7,}/g, '[redactado]'],
];

export function redactClientData(message) {
  if (typeof message !== 'string' || !message) return 'GraphQL error';
  return PII_PATTERNS.reduce(
    (out, [pattern, replacement]) => out.replace(pattern, replacement),
    message,
  );
}

/**
 * Subset SAFE de `err.extensions` que sí queremos en Sentry. Evita
 * mandar a Sentry payloads arbitrarios del backend (validaciones con
 * teléfonos / mensajes / tokens, headers reflejados, etc.).
 *
 * La allowlist no depende del scrubbing de `event.extra` en `init.js` — hoy ese
 * scrubbing es top-level y no recorre objetos anidados, y con #230 pasa a ser
 * recursivo. En los dos casos la regla aquí es la misma: se elige qué entra, en
 * vez de confiar en que algo de más arriba lo limpie. (Verificado contra la
 * denylist de #230: `extensions`, `code`, `path`, `classification`, `category`
 * y `errorCode` sobreviven al `deepScrub`.)
 *
 * Si el backend agrega un campo `extensions.code` con un enum o un
 * `extensions.classification`, son seguros para incluir. El resto se
 * descarta. Si más adelante se necesita exposure de algo específico,
 * añadirlo aquí explícitamente.
 */
function pickSafeExtensions(err) {
  const raw = err && err.extensions;
  const extensions = raw && typeof raw === 'object' ? { ...raw } : {};

  // Prisma v1 devuelve `code` en la RAÍZ del error, no en `extensions`
  // (verificado contra el backend local: `code: 3044` para el ObjectId
  // inválido). Es un clasificador sin PII y es justo lo que sustituye al dato
  // que `redactClientData` quita del mensaje — vale la pena conservarlo.
  if (
    !('code' in extensions) &&
    err &&
    (typeof err.code === 'string' || typeof err.code === 'number')
  ) {
    extensions.code = err.code;
  }

  const out = {};
  ['code', 'errorCode', 'classification', 'category'].forEach((k) => {
    if (k in extensions) {
      const v = extensions[k];
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean'
      ) {
        out[k] = v;
      }
    }
  });
  // `http` se maneja aparte porque típicamente es `{ status, headers }`
  // donde solo el status es seguro. Si el backend ocasionalmente manda un
  // scalar en `http`, lo descartamos a propósito en lugar de leakearlo.
  if (
    extensions.http &&
    typeof extensions.http === 'object' &&
    typeof extensions.http.status === 'number'
  ) {
    out.http_status = extensions.http.status;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Reporta los GraphQL errors de una response a Sentry y marca el span
 * con status ERROR. No tira el flujo si Sentry está caído.
 *
 * Dedup: usa `state.seenErrorKeys` para no capturar el mismo error N
 * veces en una subscription que emite el mismo error por payload. Key
 * = `${name}|${err.message}|${JSON.stringify(err.path)}`.
 *
 * Skipea errores "esperados" (`INVALID_TOKEN`, `UNAUTHENTICATED`) que
 * el dashboard ya maneja con logout + redirect; capturarlos aquí solo
 * genera ruido.
 *
 * Si `err` ya es una instancia de `Error`, se captura directamente para
 * conservar stack/cause. Si no, se construye un `Error` con el message.
 * Las `extensions` se filtran por `pickSafeExtensions` para no leakear
 * payloads de validación con PII.
 */
function reportGraphqlErrors(response, kind, name, state) {
  if (!response || !response.errors || response.errors.length === 0) return;

  let capturedAny = false;
  response.errors.forEach((err) => {
    try {
      if (isExpectedGraphqlError(err)) return;

      const pathKey = err && err.path ? JSON.stringify(err.path) : '';
      const message = redactClientData(err && err.message);
      // Dedup sobre el mensaje YA redactado: si el backend devuelve el mismo
      // error para 20 clientes distintos en una subscription, es un solo
      // evento, no 20.
      const key = `${name}|${message}|${pathKey}`;
      if (state.seenErrorKeys.has(key)) return;
      state.seenErrorKeys.add(key);

      // Siempre construimos un Error nuevo, incluso si `err` YA era un Error:
      // reutilizarlo conservaría su `message` sin redactar, que es justo el
      // string que se convierte en el título del issue. Tampoco lo pasamos
      // como `cause` — Sentry serializaría la cadena y el dato volvería a
      // viajar. El stack útil (los frames de este link) se genera aquí.
      const exception = new Error(message);

      Sentry.captureException(exception, {
        tags: {
          graphql_op: String(name),
          graphql_kind: String(kind),
        },
        extra: {
          path: err && err.path,
          locations: err && err.locations,
          // Solo un subset whitelisted de extensions. El backend puede
          // poner data arbitraria (incluida PII) en extensions y el
          // scrubPii() top-level de init.js no recorre objetos anidados.
          extensions: pickSafeExtensions(err),
        },
      });
      capturedAny = true;
    } catch (_e) {
      // nunca dejes que la captura tire el flujo del request
    }
  });

  // Solo marcamos el span ERROR si:
  //  - efectivamente capturamos algo (un INVALID_TOKEN aislado no debería
  //    ensuciar el status del span)
  //  - el span sigue abierto (para subs el span se cierra en el primer
  //    `next`, así que payloads posteriores no deben tocar `setStatus`).
  if (
    capturedAny &&
    !state.closed &&
    state.span &&
    typeof state.span.setStatus === 'function'
  ) {
    try {
      state.span.setStatus({
        code: SPAN_STATUS_ERROR,
        message: 'graphql_error',
      });
    } catch (_e) {
      // best-effort
    }
  }
}

export const sentryApolloLink = new ApolloLink((operation, forward) => {
  const kind = extractKind(operation.query);
  const name = operation.operationName || 'anonymous';
  const isSubscription = kind === 'subscription';

  // 1. Span por operación (inactivo para no contaminar el active scope).
  //    Se crea ANTES de inyectar headers para que `sentry-trace` y
  //    `baggage` representen a este span como parent del request HTTP.
  const span = startGraphqlSpan(kind, name);
  const state = {
    span,
    startedAt: Date.now(),
    closed: false,
    // Dedup de errores por operación: evita N captures cuando el server
    // devuelve el mismo error por cada payload en una subscription.
    seenErrorKeys: new Set(),
  };

  // 2. Inyectar headers de trace SOLO para operaciones HTTP. Las
  //    subscriptions viajan por WebSocketLink y `setContext` no afecta
  //    los `connectionParams` del WS — la propagación para subs queda
  //    como follow-up.
  if (!isSubscription) {
    const traceHeaders = buildTraceHeaders(span);
    if (Object.keys(traceHeaders).length > 0) {
      operation.setContext(({ headers = {} } = {}) => ({
        headers: { ...headers, ...traceHeaders },
      }));
    }
  }

  // 3. Si por configuración incorrecta este link queda como terminating
  //    (sin `forward` válido), no podemos delegar. Cerrar el span y
  //    emitir un error explícito al consumidor para que el wire-up se
  //    detecte temprano en lugar de fallar silenciosamente.
  if (typeof forward !== 'function') {
    finalizeSpan(state);
    return new Observable((observer) => {
      observer.error(new Error(
        '[sentryApolloLink] forward is not a function — link incorrectly placed as terminating link',
      ));
    });
  }

  // 4. Envolvemos el observable upstream con uno propio para poder cerrar
  //    el span en el momento correcto según el kind:
  //    - Queries / mutations: cerrar en `complete` / `error` / `unsubscribe`.
  //      En la práctica `complete` llega justo después del único `next`.
  //    - Subscriptions: cerrar el span en el PRIMER `next` (representa el
  //      tiempo "subscribe → primer payload" = establishment del stream).
  //      Mantener el span abierto durante minutos/horas no es semántico de
  //      span en Sentry y puede degradar la UI de tracing. Los payloads
  //      siguientes siguen pasando por `reportGraphqlErrors` (con dedup),
  //      solo que ya no tocan el span (cerrado).
  return new Observable((observer) => {
    let upstreamSub;
    try {
      upstreamSub = forward(operation).subscribe({
        next(response) {
          // Reportar GraphQL errors en CUALQUIER payload con dedup. Si el
          // span ya está cerrado (subs después del primer payload),
          // reportGraphqlErrors no toca `state.span.setStatus`.
          reportGraphqlErrors(response, kind, name, state);
          // Subs: cerrar el span ANTES de propagar `next` al downstream.
          // Si el observer río abajo tira síncronamente en el primer
          // payload, igualmente queremos haber cerrado el span — `state.closed`
          // hace `finalizeSpan` idempotente, así que un cierre tardío en
          // `unsubscribe`/`error` no duplica el `span.end()`.
          if (isSubscription && !state.closed) {
            finalizeSpan(state);
          }
          observer.next(response);
        },
        error(err) {
          // Network/protocol errors: NO los capturamos aquí (handled by
          // apolloQuery/apolloMutation). Solo marcamos el span y cerramos.
          if (state.span && typeof state.span.setStatus === 'function') {
            try {
              state.span.setStatus({
                code: SPAN_STATUS_ERROR,
                message: 'network_error',
              });
            } catch (_e) {
              // best-effort
            }
          }
          finalizeSpan(state);
          observer.error(err);
        },
        complete() {
          finalizeSpan(state);
          observer.complete();
        },
      });
    } catch (e) {
      // Si subscribe lanza síncronamente (raro), cerrar el span y
      // propagar el error al consumidor.
      finalizeSpan(state);
      observer.error(e);
      return undefined;
    }

    return () => {
      // Teardown: el consumidor canceló la sub (o desmonta). Cerrar
      // span (si seguía abierto) y limpiar el upstream.
      finalizeSpan(state);
      if (upstreamSub && typeof upstreamSub.unsubscribe === 'function') {
        try {
          upstreamSub.unsubscribe();
        } catch (_e) {
          // best-effort cleanup
        }
      }
    };
  });
});

export default sentryApolloLink;
